import { IconNames } from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { MSAA_QUALITY, Texture } from 'pixi.js';

interface IconSettings {
  readonly fill?: string;
  readonly size?: number;
}

type IconTextureMap = Record<IconNames, Texture>;
type IconTextureCacheMap = Map<IconSettings, Partial<IconTextureMap>>;

export const iconSize = 24;
export const defaultIconSettings: Required<IconSettings> = {
  fill: typography.color.primary,
  size: iconSize,
};

export const iconTextureCacheMap: IconTextureCacheMap = new Map();

export const getIconTextures = async (
  attributes: IconNames[],
  iconSettings: IconSettings = defaultIconSettings,
): Promise<IconTextureMap> => {
  if (!iconTextureCacheMap.has(iconSettings)) {
    iconTextureCacheMap.set(iconSettings, {});
  }

  const cache = iconTextureCacheMap.get(
    iconSettings,
  ) as Partial<IconTextureMap>;

  const iconLoadPromises = attributes.reduce<
    Partial<Record<IconNames, Promise<Texture>>>
  >((promiseMap, iconName) => {
    const cachedIconTexture = cache[iconName];

    if (cachedIconTexture) {
      promiseMap[iconName] = Promise.resolve(cachedIconTexture);
    } else {
      const url = getIconAsBlobURL(iconName, iconSettings);
      const texture = Texture.fromURL(url, {
        multisample: MSAA_QUALITY.HIGH,
      });

      promiseMap[iconName] = texture;
    }

    return promiseMap;
  }, {});

  const iconLoadKeys = Object.keys(iconLoadPromises);
  const iconLoadValues = Object.values(iconLoadPromises);

  return Promise.all(iconLoadValues).then((values) => {
    const icons = {};

    iconLoadKeys.forEach((name, index) => {
      const texture = values[index];
      const iconName = name as IconNames;

      cache[iconName] = texture;
      icons[iconName] = texture;
    });

    return icons as IconTextureMap;
  });
};

export const getIconAsBlobURL = (
  name: IconNames,
  iconSettings: IconSettings = defaultIconSettings,
): string | never => {
  if (!name) {
    throw new Error('Missing icon name');
  }

  const symbol = document.querySelector(`#icon-${name}-${iconSize}`) as
    | SVGSymbolElement
    | undefined;

  if (!symbol) {
    throw new Error(`No icon with name "${name}"`);
  }

  const viewBox = symbol.getAttribute('viewBox') || '';
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

  const { fill = defaultIconSettings.fill, size = defaultIconSettings.size } =
    iconSettings;

  svg.setAttribute('viewBox', viewBox);
  svg.setAttribute('fill', fill);
  svg.setAttribute('width', `${size * 2}px`);
  svg.setAttribute('height', `${size * 2}px`);
  // TODO: [TEAMS-12082] Investigate improper use of innerHTML
  svg.innerHTML = symbol.innerHTML;

  const serializer = new XMLSerializer();
  const svgAsString = serializer.serializeToString(svg);

  const blob = new Blob([svgAsString], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);

  return url;
};
